import React, { useCallback, useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import keycodes from '../../constants/key-codes';
import { APPEARANCES, SIZES, ALIGN } from './constants';
import parseValue from './helpers/parse-value';
const getMin = (min, value) => {
  if (typeof min === 'function') {
    return min(value);
  }

  return min;
};

const getMax = (max, value) => {
  if (typeof max === 'function') {
    return max(value);
  }

  return max;
};

const defaultValueFormatter = (v) => v;

const ValueInput = ({
  value = 0,
  min = Number.MIN_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER,
  step = 0,
  appearance = APPEARANCES.white,
  align = ALIGN.right,
  size = SIZES.tiny,
  supportedUnits = [],
  fullWidth = false,
  bold = false,
  disabled = false,
  borderless = false,
  noAutocomplete = false,
  name = '',
  onChange,
  valueFormatter = defaultValueFormatter,
  onInputChange = Function.prototype,
  onBlur = Function.prototype,
  onFocus = Function.prototype,
}) => {
  const {
    value: parsedValue,
    unit: parsedUnit,
    isValid: isParsedValueValid,
  } = parseValue(value.toString(), supportedUnits);

  const inputRef = useRef(null);
  const [currentUnit, setCurrentUnit] = useState(parsedUnit);
  const [inputValue, setInputValue] = useState(value);
  const [lastValidValue, setLastValidValue] = useState(isParsedValueValid ? parsedValue : value);
  const [lastValidUnit, setLastValidUnit] = useState(parsedUnit);
  const [isEditing, setIsEditing] = useState(false);

  const getNewValue = useCallback(
    (target) => {
      const targetValue = target.value.toString();
      const {
        value: newParsedValue,
        unit: newParsedUnit,
        isValid,
      } = parseValue(targetValue, supportedUnits);
      let newValue;
      if (isValid) {
        const clampedValue = Math.min(
          Math.max(newParsedValue, getMin(min, targetValue)),
          getMax(max, targetValue)
        );
        newValue = valueFormatter(clampedValue);
      } else {
        newValue = lastValidValue;
      }
      const newUnit = isValid ? newParsedUnit : lastValidUnit;

      return { value: newValue, unit: newUnit, isValid };
    },
    [lastValidUnit, lastValidValue, max, min, supportedUnits, valueFormatter]
  );

  const handleChange = useCallback(
    (newValue, newUnit = '') => {
      setLastValidValue(newValue);
      setLastValidUnit(newUnit);
      setInputValue(`${newValue}${newUnit}`);
      setIsEditing(false);

      onChange(newUnit ? `${newValue}${newUnit}` : newValue);

      if (newUnit !== currentUnit) {
        setCurrentUnit(newUnit);
      }
    },
    [onChange, currentUnit]
  );

  const handleFocus = useCallback(() => {
    setIsEditing(true);
    setInputValue(value.toString());
    onFocus();
  }, [value, onFocus]);

  const handleBlur = useCallback(
    ({ target }) => {
      const { value: newValue, unit: newUnit } = getNewValue(target);

      handleChange(newValue, newUnit);
      onBlur(newUnit ? `${newValue}${newUnit}` : newValue);
    },
    [handleChange, onBlur, getNewValue]
  );

  const handleKeyDown = useCallback(
    ({ target, keyCode }) => {
      if (keyCode === keycodes.ENTER) {
        const { value: newValue, unit: newUnit } = getNewValue(target);

        handleChange(newValue, newUnit);
        inputRef.current.blur();
      }

      if (keyCode === keycodes.UP) {
        const { value: newValue, unit: newUnit } = getNewValue(target);

        const updatedValue = Math.min(
          Math.round((Number(newValue) + step) * 100) / 100,
          getMax(max, String(newValue))
        );

        handleChange(updatedValue, newUnit);
      }

      if (keyCode === keycodes.DOWN) {
        const { value: newValue, unit: newUnit } = getNewValue(target);

        const updatedValue = Math.max(
          Math.round((Number(newValue) - step) * 100) / 100,
          getMin(min, String(newValue))
        );

        handleChange(updatedValue, newUnit);
      }
    },
    [handleChange, getNewValue, step, max, min]
  );

  const handleInputChange = useCallback(
    ({ target }) => {
      setIsEditing(true);
      onInputChange(target.value);
      setInputValue(target.value);
    },
    [onInputChange]
  );

  useEffect(() => {
    if (!isEditing) {
      setInputValue(value);
    }
  }, [value, isEditing]);

  const classes = classNames(
    'ws-ui-value-input',
    `ws-ui-value-input--${appearance}`,
    `ws-ui-value-input--${size}`,
    {
      'ws-ui-value-input--disabled': disabled,
      'ws-ui-value-input--full-width': fullWidth,
      'ws-ui-value-input--borderless': borderless,
    }
  );

  const inputClasses = classNames(
    'ws-ui-value-input__input',
    `ws-ui-value-input__input--align-${align}`,
    {
      'ws-ui-value-input__input--bold': bold,
    }
  );

  return (
    <div className={classes}>
      <input
        ref={inputRef}
        className={inputClasses}
        value={inputValue}
        disabled={disabled}
        name={name}
        {...(noAutocomplete && { autoComplete: 'off', autoCorrect: 'off', spellCheck: false })}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
};

export default ValueInput;
